import { Box } from '@trmediaab/zebra';
import type { VerificationInputProps } from 'react-verification-input';
import ReactVerificationInput from 'react-verification-input';
import { useTheme } from 'styled-components';

/**
 * VerificationCodeInput component renders a styled input field for entering verification codes.
 * It uses the `ReactVerificationInput` component and applies custom styles to it.
 *
 * @param props - The properties for the VerificationCodeInput component, excluding `classNames`.
 *
 * @returns A styled Box component containing the `ReactVerificationInput` with custom styles.
 *
 * The component applies the following custom styles:
 * - Overrides container styles to set width, height, and gap.
 * - Customizes the first and last character divs to have rounded corners.
 * - Sets disabled styles for the input fields.
 * - Overrides and adds to character styles, including background color, font size, border color, width, height, and line height.
 * - Overrides inactive background color to match the selected background color.
 * - Overrides selected styles and adds a fake caret animation when the field is empty.
 */
export default function VerificationCodeInput(
  props: Omit<VerificationInputProps, 'classNames'>,
) {
  const theme = useTheme();
  const fieldSize =
    typeof theme.sizes.buttonHeights.xlarge === 'number'
      ? theme.sizes.buttonHeights.xlarge
      : 40;

  return (
    <Box
      sx={{
        // Animation for the fake caret
        '@keyframes blink': {
          '0%': {
            opacity: 1,
          },
          '50%': {
            opacity: 0,
          },
        },
        '.verification-input__container': {
          // Override container styles
          width: 'auto',
          height: 'auto',
          gap: 0,

          // Custom style first and last character div (faux input fields)
          'div:first-of-type': {
            borderTopLeftRadius: '6px',
            borderBottomLeftRadius: '6px',
            marginLeft: 0,
          },
          'div:last-of-type': {
            borderTopRightRadius: '6px',
            borderBottomRightRadius: '6px',
          },
          // Set disabled styles
          'input[disabled] ~ .verification-input__character': {
            opacity: 0.5,
            cursor: 'not-allowed',
          },
        },
        // Override and add to character styles
        '.verification-input__character': {
          backgroundColor: 'checkout.verificationInputBg',
          fontSize: 'base',
          borderColor: 'checkout.verificationInputBorder',
          borderWidth: '1px',
          color: 'checkout.verificationInputText',
          marginLeft: '-1px',
          width: fieldSize,
          height: fieldSize,
          lineHeight: `${fieldSize}px`,
        },
        // Override inactive background to be the same as the selected
        '.verification-input__character--inactive': {
          backgroundColor: 'checkout.verificationInputBg',
        },
        // Override selected styles and add a fake caret when it's empty
        '.verification-input__character--selected': {
          outlineColor: 'checkout.verificationInputBorder',
          zIndex: 1,
          position: 'relative',
          '&:not(.verification-input__character--filled)::after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            display: 'block',
            width: '1px',
            height: fieldSize / 2,
            bg: 'checkout.verificationInputText',
            transform: 'translate(-50%, -50%)',
            animation: 'blink 1s infinite step-end',
          },
        },
      }}
    >
      <ReactVerificationInput
        placeholder=""
        classNames={{
          container: 'verification-input__container',
          character: 'verification-input__character',
          characterInactive: 'verification-input__character--inactive',
          characterSelected: 'verification-input__character--selected',
          characterFilled: 'verification-input__character--filled',
        }}
        {...props}
      />
    </Box>
  );
}
